// /  <reference types="@types/google.maps" />
import 'react-phone-number-input/style.css';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';
import { classNames } from 'primereact/utils';
import { useEffect, useState } from 'react';
import { FieldError, FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { useGoogleAPILoadingScript } from '../../../hooks/useGoogleMapsScript';
import { ILocation } from '../../../shared/interfaces/IFootprint';
import ErrorMessage from '../../error-message';

interface IPlaceDetails extends ILocation {
  place?: google.maps.places.Place;
}

export type GooglePlacesAutoCompleteInputProps<T extends FieldValues> = Omit<
  AutoCompleteProps,
  'name'
> & {
  label?: string;
  name: string;
  value?: ILocation;
} & UseControllerProps<T>;

const GooglePlacesAutoCompleteInput = <T extends FieldValues>({
  label,
  ...props
}: GooglePlacesAutoCompleteInputProps<T>) => {
  const { fieldState, field } = useController({
    control: props.control,
    name: props.name,
  });

  const [filteredSuggestions, setFilteredSuggestions] = useState<IPlaceDetails[]>([]);

  // Dynamic Google Maps script loading check the status and then add if not present
  const googleApiLoadingStatus = useGoogleAPILoadingScript();

  useEffect(() => {
    const initialize = async () => {
      if (googleApiLoadingStatus === 'error') {
        throw new Error('Failed to initialize Google Places Autocomplete:');
      }
    };

    initialize();
  }, [googleApiLoadingStatus]);

  const searchPlaces = async (query: string) => {
    const { AutocompleteSessionToken, AutocompleteSuggestion } =
      (await window.google.maps.importLibrary('places')) as google.maps.PlacesLibrary;

    // Create a session token.
    const token = new AutocompleteSessionToken();
    // Add the token to the request.
    const request = {
      input: query,
      language: 'en-US',
      sessionToken: token,
    };
    // Fetch autocomplete suggestions.
    const { suggestions: predictions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(
      request,
    );
    const suggestions: IPlaceDetails[] = [];
    for (const prediction of predictions) {
      const placePrediction = prediction.placePrediction;

      suggestions.push({
        name: placePrediction?.text.toString(),
        coordinates: { latitude: undefined, longitude: undefined },
        place: placePrediction?.toPlace(),
      });
    }
    setFilteredSuggestions(suggestions);
  };

  const onCompleteMethod = (event: { query: string }) => {
    searchPlaces(event.query);
  };

  const onPlaceSelected = async (event: IPlaceDetails) => {
    if (event.place) {
      await event.place?.fetchFields({
        fields: ['location'],
      });
      field.onChange({
        // if location details are not retrieved by getPlace API then re-setting the field to empty values
        name: event.place?.location ? event.name ?? '' : '',
        coordinates: event.place?.location
          ? {
              latitude: event.place?.location?.lat(),
              longitude: event.place?.location?.lng(),
            }
          : undefined,
      });
    } else {
      field.onChange({
        // if location details are not retrieved by getPlace API then re-setting the field to empty values
        name: '',
        coordinates: {
          latitude: undefined,
          longitude: undefined,
        },
      });
    }
  };

  return (
    <div className='field p-fluid' id='google-places-autocomplete-input'>
      <span className={classNames('p-float-label')}>
        <AutoComplete
          id={field.name}
          invalid={fieldState.invalid}
          value={field.value}
          suggestions={filteredSuggestions}
          completeMethod={onCompleteMethod}
          field='name'
          onChange={(e) => {
            return field.onChange(e.value);
          }}
          onSelect={(e) => {
            onPlaceSelected(e.value);
          }}
        />

        {label && <label htmlFor={props.name}>{label}</label>}
      </span>

      {/* Hidden input to enable focus & auto-scroll on error */}
      <input readOnly className='opacity-0 p-0 m-0 w-0 h-0 absolute' ref={field.ref} />

      {fieldState?.error && (
        <div className='py-2'>
          <ErrorMessage
            message={
              fieldState.error?.message ||
              (fieldState.error as unknown as { [key: string]: FieldError })?.name?.message ||
              (fieldState.error as unknown as { [key: string]: FieldError })?.coordinates
                ?.message ||
              ''
            }
          />
        </div>
      )}
    </div>
  );
};

export default GooglePlacesAutoCompleteInput;
